import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppButton, AppField, AppModal, AppSelect, AppTextarea } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import { LifecycleStrip } from "../../../shared/components/LifecycleStrip";
import { IconCheck, IconChevronRight, IconHourglass, IconInfo } from "../../../shared/components/icons";
import { listLocalLeads, type LocalLeadStatus } from "../store";
import { FieldWorkspace } from "../../../shared/components/FieldWorkspace";
import { factsFromLocalLead, resolveJourney } from "../journey";
import { api } from "../../../shared/api/client";
import type { Lead } from "../types";

/**
 * Field home — local index of leads captured on this device (FieldShell home).
 * Mode A: lifecycle strip · Mode B: local Capture→KYC steps on child screens · Mode C: Continue journey.
 */

const STATUS_BADGE: Record<LocalLeadStatus, { tone: string; label: string }> = {
  draft: { tone: "neutral", label: "draft" },
  "pending-sync": { tone: "warning", label: "pending sync" },
  synced: { tone: "info", label: "synced" },
  "pending-duplicate-check": { tone: "warning", label: "check pending" },
  "kyc-submitted": { tone: "success", label: "KYC submitted" },
  "closed-merge": { tone: "neutral", label: "closed — merged" },
  "closed-reject": { tone: "neutral", label: "closed — rejected" },
};

export function FieldLeadsHome() {
  useFieldHeader("Lead & KYC Workspace", "Queue, review, and progress customer onboarding");
  useFieldJourney({ flow: "hub", hideBack: true });
  const navigate = useNavigate();
  const localLeads = listLocalLeads();
  const [serverLeads, setServerLeads] = useState<Lead[]>([]);
  const [serverError, setServerError] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(
    localLeads[0] ? `local:${localLeads[0].localId}` : null,
  );
  const [editing, setEditing] = useState<Lead | null>(null);
  const [editStatus, setEditStatus] = useState("new");
  const [editNotes, setEditNotes] = useState("");
  const [editError, setEditError] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const loadServerLeads = async () => {
    setServerError("");
    try {
      const data = await api.get<{ leads: Lead[] }>("/leads?per_page=50");
      setServerLeads((data.leads ?? []).slice(0, 50));
    } catch (ex: unknown) {
      setServerError(ex instanceof Error ? ex.message : "Server leads could not be loaded");
    }
  };

  useEffect(() => {
    void loadServerLeads();
  }, []);

  const queue = useMemo(() => {
    const syncedIds = new Set(localLeads.map((lead) => lead.serverId).filter((id): id is number => id != null));
    return [
      ...localLeads.map((lead) => ({
        key: `local:${lead.localId}`,
        local: lead,
        server: lead.serverId != null ? serverLeads.find((item) => item.id === lead.serverId) : undefined,
      })),
      ...serverLeads
        .filter((lead) => !syncedIds.has(lead.id))
        .map((lead) => ({ key: `server:${lead.id}`, local: undefined, server: lead })),
    ];
  }, [localLeads, serverLeads]);

  useEffect(() => {
    if (!selectedKey && queue[0]) setSelectedKey(queue[0].key);
  }, [queue, selectedKey]);

  const selected = queue.find((lead) => lead.key === selectedKey) ?? queue[0] ?? null;
  const selectedName = selected?.local?.name
    ?? (selected?.server ? `${selected.server.first_name} ${selected.server.last_name}` : "");
  const selectedStatus = selected?.local?.status ?? selected?.server?.status ?? "new";
  const selectedBadge = selected?.local
    ? STATUS_BADGE[selected.local.status]
    : { tone: selectedStatus === "converted" ? "success" : "info", label: selectedStatus.replace(/_/g, " ") };

  const journey = useMemo(
    () =>
      resolveJourney(
        selected?.local
          ? factsFromLocalLead(selected.local)
          : { leadCaptured: false },
      ),
    [selected],
  );

  const openLead = () => {
    if (!selected) return;
    const id = selected.server?.id ?? selected.local?.serverId ?? selected.local?.localId;
    if (id != null) navigate(`/field/leads/${id}/duplicates`);
  };

  const openEdit = (lead: Lead) => {
    setEditing(lead);
    setEditStatus(lead.status || "new");
    setEditNotes(lead.notes || "");
    setEditError("");
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setEditBusy(true);
    setEditError("");
    try {
      const data = await api.put<{ lead: Lead }>(`/leads/${editing.id}`, {
        status: editStatus,
        notes: editNotes.trim(),
      });
      setServerLeads((items) => items.map((lead) => lead.id === editing.id ? data.lead : lead));
      setEditing(null);
    } catch (ex: unknown) {
      setEditError(ex instanceof Error ? ex.message : "Lead could not be updated");
    } finally {
      setEditBusy(false);
    }
  };

  return (
    <>
      {editing ? (
        <AppModal open={!!editing} onClose={() => setEditing(null)} title={`Edit notes — ${editing.first_name} ${editing.last_name}`}>
          <form onSubmit={saveEdit}>
            <AppField label="Status">
              <AppSelect value={editStatus} onChange={(e) => setEditStatus(e.target.value)} disabled={editBusy}>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="interested">Interested</option>
                <option value="converted">Converted</option>
                <option value="lost">Lost</option>
              </AppSelect>
            </AppField>
            <AppField label="Notes" error={editError}>
              <AppTextarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={4} disabled={editBusy} />
            </AppField>
            <div className="pg-modal-actions">
              <AppButton type="submit" disabled={editBusy}>{editBusy ? "Saving…" : "Save"}</AppButton>
              <AppButton type="button" variant="secondary" onClick={() => setEditing(null)} disabled={editBusy}>Cancel</AppButton>
            </div>
          </form>
        </AppModal>
      ) : null}
      <FieldWorkspace
      left={
        <>
          <div style={{ marginBottom: "var(--pg-s3)" }}>
            <h3 style={{ margin: 0 }}>Lead queue</h3>
            <p style={{ margin: "6px 0 0", color: "var(--pg-text-muted)", fontSize: "var(--pg-fs-meta)" }}>
              Capture leads, run duplicate checks, and complete KYC submissions.
            </p>
          </div>
          {serverError ? (
            <div className="pg-inline-error" role="alert" style={{ marginBottom: 8 }}>
              Server leads unavailable: {serverError}
            </div>
          ) : null}
          {queue.length === 0 ? (
            <div className="pg-state" style={{ padding: "var(--pg-s4)" }}>
              <span className="state-icon info">
                <IconInfo size={32} />
              </span>
              <h2>No leads yet</h2>
              <p>Start with a new lead capture.</p>
            </div>
          ) : (
            queue.map((lead) => {
              const status = lead.local?.status ?? lead.server?.status ?? "new";
              const badge = lead.local
                ? STATUS_BADGE[lead.local.status]
                : { tone: status === "converted" ? "success" : "info", label: status.replace(/_/g, " ") };
              const name = lead.local?.name
                ?? (lead.server ? `${lead.server.first_name} ${lead.server.last_name}` : "Unnamed lead");
              return (
                <button
                  key={lead.key}
                  type="button"
                  className="pg-lead-row"
                  onClick={() => setSelectedKey(lead.key)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    marginBottom: "8px",
                    outline: selected?.key === lead.key ? "2px solid var(--pg-green)" : "none",
                  }}
                >
                  <span>
                    <div className="lead-name">{name}</div>
                    <div className="lead-meta">
                      {[lead.local?.phone ?? lead.server?.phone, lead.local?.ward ?? lead.server?.ward].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </span>
                  <span className="lead-tail">
                    <span className={`pg-badge ${badge.tone}`}>
                      {status === "kyc-submitted" || status === "converted" ? (
                        <IconCheck size={12} />
                      ) : status === "pending-sync" || status === "pending-duplicate-check" ? (
                        <IconHourglass size={12} />
                      ) : (
                        <IconInfo size={12} />
                      )}
                      {badge.label}
                    </span>
                    <IconChevronRight size={14} />
                  </span>
                </button>
              );
            })
          )}
        </>
      }
      right={
        <>
          <LifecycleStrip journey={journey} title="Customer journey" />

          {!selected ? (
            <div className="pg-state" style={{ padding: "var(--pg-s5) var(--pg-s4)" }}>
              <span className="state-icon info">
                <IconInfo size={40} />
              </span>
              <h2>No lead selected</h2>
              <p>Create a lead to begin onboarding.</p>
            </div>
          ) : (
            <div className="pg-summary">
              <div className="row">
                <span className="k">Lead</span>
                <span className="v">{selectedName}</span>
              </div>
              <div className="row">
                <span className="k">Phone</span>
                <span className="v">{selected.local?.phone ?? selected.server?.phone ?? "—"}</span>
              </div>
              <div className="row">
                <span className="k">Ward</span>
                <span className="v">{selected.local?.ward ?? selected.server?.ward ?? "—"}</span>
              </div>
              <div className="row">
                <span className="k">Status</span>
                <span className="v">{selectedBadge.label}</span>
              </div>
              <div className="row">
                <span className="k">Local steps</span>
                <span className="v">Capture → Duplicates → Review → KYC</span>
              </div>
              <div className="row">
                <span className="k">Next owner</span>
                <span className="v">
                  {selectedStatus === "kyc-submitted" || selectedStatus === "converted" ? "Office KYC reviewer" : "Field sales / commercial"}
                </span>
              </div>
            </div>
          )}

          <div className="pg-actions" style={{ position: "static", paddingBottom: 0 }}>
            <AppButton type="button" onClick={() => navigate("/field/leads/new")}>
              New Lead
            </AppButton>
            {selected ? (
              <AppButton
                variant="secondary"
                type="button"
                onClick={openLead}
              >
                Continue selected lead
              </AppButton>
            ) : null}
            {selected?.server ? (
              <AppButton variant="secondary" type="button" onClick={() => openEdit(selected.server!)}>
                Edit notes
              </AppButton>
            ) : null}
            {selectedStatus === "kyc-submitted" ? (
              <AppButton
                variant="secondary"
                type="button"
                onClick={() => navigate(journey.next?.href ?? "/customers")}
              >
                Continue to next
              </AppButton>
            ) : null}
          </div>
        </>
      }
      />
    </>
  );
}

/**
 * Lead-closed screen — terminal state after a merge/reject duplicate decision
 * (handoff TASK-002 S3 success path for merge/reject).
 */
export function FieldLeadClosed() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const decision = searchParams.get("decision");
  const customerId = searchParams.get("customer_id");
  useFieldHeader(
    "Lead closed",
    decision === "merge" ? "Merged into existing customer" : "Rejected — not a duplicate",
  );
  useFieldJourney({ flow: "leads", hideBack: false, backTo: "/field/leads" });

  return (
    <div className="pg-state">
      <span className="state-icon success">
        <IconCheck size={40} />
      </span>
      <h2>{decision === "merge" ? "Records merged" : "Lead closed"}</h2>
      <p>
        {decision === "merge"
          ? "The lead was merged into the existing customer record with an audit trail."
          : "The lead was closed as not-a-duplicate with an audit trail."}
      </p>
      <AppButton type="button" onClick={() => navigate("/field/leads")}>
        Back to Leads
      </AppButton>
      {decision === "merge" ? (
        <AppButton
          variant="secondary"
          type="button"
          onClick={() => navigate(
            customerId ? `/customers/${customerId}?contract=activate` : "/customers",
          )}
        >
          Continue to contract
        </AppButton>
      ) : null}
    </div>
  );
}
